// مأموریت خودرو
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePagedList, CountBadge } from '../../components/paging';
import { ExcelButton } from '../../components/excel';
import { openReport } from '../../components/report';
import { useFormButtons } from '../../utils/vbPerms';
import {
  API, qs, toFa, curYear, todayFa, Grid, Field, TextIn, DateIn, Lookup, SuggestIn,
  Modal, DeleteDialog, MsgDialog, excelCols, smallBtn,
} from './carsShared';

const FORM = 'FRMCARMISSION';
const BUTTONS = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL', 'BTNREPORT'];
const MISSION_BASE = '/mission';
const MAPI = (path, opts) => API(`${MISSION_BASE}${path}`, opts);

const ROW_COLORS = {
  done: '#c8e6c9',
  manager_agreed: '#fff9c4',
  dept_agreed: '#bbdefb',
  pending: null,
};

const EMPTY = () => ({
  car: { id: 0, title: '' },
  department: { id: 0, title: '' },
  city: { id: 0, title: '' },
  manager: { id: 0, title: '' },
  request_no: '', request_date: '',
  mission_subject: '', from_date: '', to_date: '', mission_duration: '',
  department_agreement_date: '', manager_agreement_date: '', done_confirmation_date: '',
  exit_time: '', enter_time: '',
  serial_no: '', insert_user: '', insert_date: '',
  driver: '', dept_manager: '',
});

function rowToForm(r) {
  return {
    car: { id: r.CarId || 0, title: r['نام خودرو'] || '' },
    department: { id: r.DepartmentId || 0, title: r['واحد اعزام کننده'] || '' },
    city: { id: r.CityId || 0, title: r['محل ماموریت'] || '' },
    manager: { id: r.ManagerId || 0, title: r['موافقت کننده'] || '' },
    request_no: r['شماره درخواست'] ?? '', request_date: r['تاریخ درخواست'] || '',
    mission_subject: r['موضوع ماموریت'] || '', from_date: r['از تاریخ'] || '', to_date: r['تا تاریخ'] || '',
    mission_duration: r['مدت'] ?? '',
    department_agreement_date: r['تاریخ موافقت مسئول واحد'] || '',
    manager_agreement_date: r['تاریخ موافقت موافقت کننده'] || '',
    done_confirmation_date: r['تاریخ تایید انجام ماموریت'] || '',
    exit_time: r['ساعت خروج'] || '', enter_time: r['ساعت ورود'] || '',
    serial_no: r['سریال'] ?? '', insert_user: r['کاربر ثبت کننده'] || '',
    insert_date: r['تاریخ ثبت'] || '',
    driver: r['نام راننده'] || '', dept_manager: r['مسئول واحد'] || '',
    car_kind: r['نوع مالکیت'] || '',
    _perms: r._perms || {},
  };
}

/* ────────────── فرم ────────────── */
function MissionForm({ form, F, mode, onCarChange, onDeptChange }) {
  const readOnly = mode === 'view';
  const canEditFields = mode === 'new' || (mode === 'edit' && form._perms?.can_edit_fields);

  return (
    <>
      <div style={ui.row}>
        <Field label="سریال">
          <TextIn num value={form.serial_no} disabled onChange={() => {}} />
        </Field>
        <Field label="شماره درخواست">
          <TextIn num value={form.request_no} disabled={!canEditFields} onChange={v => F('request_no', v)} />
        </Field>
        <Field label="تاریخ درخواست">
          <DateIn value={form.request_date} disabled={!canEditFields} onChange={v => F('request_date', v)} />
        </Field>
      </div>
      <div style={ui.row}>
        <Field label="واحد اعزام کننده" flex={2}>
          <Lookup path={`${MISSION_BASE}/lookup/department/`} value={form.department}
            disabled={!canEditFields} onChange={v => { F('department', v); onDeptChange && onDeptChange(v.id); }} />
        </Field>
        <Field label="مسئول واحد">
          <TextIn value={form.dept_manager} disabled onChange={() => {}} />
        </Field>
      </div>
      <div style={ui.row}>
        <Field label="خودرو" flex={2}>
          <Lookup path={`${MISSION_BASE}/car-lookup/`} value={form.car}
            disabled={!canEditFields} onChange={v => { F('car', v); onCarChange && onCarChange(v.id); }} />
        </Field>
        <Field label="نوع مالکیت">
          <TextIn value={form.car_kind || ''} disabled onChange={() => {}} />
        </Field>
        <Field label="نام راننده">
          <TextIn value={form.driver} disabled onChange={() => {}} />
        </Field>
      </div>
      <div style={ui.row}>
        <Field label="محل ماموریت">
          <Lookup path={`${MISSION_BASE}/city-lookup/`} value={form.city}
            disabled={!canEditFields} onChange={v => F('city', v)} />
        </Field>
        <Field label="موضوع ماموریت" flex={2}>
          <SuggestIn path={`${MISSION_BASE}/remarks/`} value={form.mission_subject}
            disabled={!canEditFields} onChange={v => F('mission_subject', v)} />
        </Field>
      </div>
      <div style={ui.row}>
        <Field label="از تاریخ">
          <DateIn value={form.from_date} disabled={!canEditFields} onChange={v => F('from_date', v)} />
        </Field>
        <Field label="تا تاریخ">
          <DateIn value={form.to_date} disabled={!canEditFields} onChange={v => F('to_date', v)} />
        </Field>
        <Field label="مدت (روز)">
          <TextIn num value={form.mission_duration} disabled={!canEditFields} onChange={v => F('mission_duration', v)} />
        </Field>
      </div>
      <div style={ui.row}>
        <Field label="موافقت کننده">
          <Lookup path={`${MISSION_BASE}/lookup/people/`} value={form.manager}
            disabled={!canEditFields} onChange={v => F('manager', v)} />
        </Field>
      </div>
      <div style={ui.row}>
        <Field label="ساعت خروج">
          <TextIn value={form.exit_time} disabled={!canEditFields} onChange={v => F('exit_time', v)} />
        </Field>
        <Field label="ساعت ورود">
          <TextIn value={form.enter_time} disabled={!canEditFields} onChange={v => F('enter_time', v)} />
        </Field>
      </div>

      {/* وضعیت تأیید — فقط خواندنی در فرم، تغییر از طریق دکمه‌های مخصوص */}
      <div style={{ ...ui.row, borderTop: '1px solid #e0e0e0', paddingTop: '12px', marginTop: '8px' }}>
        <Field label="کاربر ثبت‌کننده">
          <TextIn value={form.insert_user} disabled onChange={() => {}} />
        </Field>
        <Field label="تاریخ ثبت">
          <TextIn value={form.insert_date} disabled onChange={() => {}} />
        </Field>
      </div>
    </>
  );
}

/* ────────────── صفحهٔ اصلی ────────────── */
export default function CarMission({ user, onBack }) {
  const fb = useFormButtons(FORM, BUTTONS);
  const [year, setYear] = useState(curYear());
  const [cols, setCols] = useState([]);
  const [sel, setSel] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY());
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  // فیلترهای وضعیت
  const [showAll, setShowAll] = useState(true);
  const [isDeptAgr, setIsDeptAgr] = useState(false);
  const [notDeptAgr, setNotDeptAgr] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [notDone, setNotDone] = useState(false);
  const [isMgrAgr, setIsMgrAgr] = useState(false);
  const [notMgrAgr, setNotMgrAgr] = useState(false);

  const filterParams = () => ({
    show_all: showAll ? '1' : '0',
    is_department_agreement: isDeptAgr ? '1' : '0',
    not_department_agreement: notDeptAgr ? '1' : '0',
    is_done_confirmation: isDone ? '1' : '0',
    not_done_confirmation: notDone ? '1' : '0',
    is_manager_agreement: isMgrAgr ? '1' : '0',
    not_manager_agreement: notMgrAgr ? '1' : '0',
  });

  const L = usePagedList((page, o) =>
    MAPI(`/?${qs({ page, year: o.year, ...o.filters })}`)
      .then(j => { if (j.ok && page === 1) setCols(j.columns || []); return j; }));

  const reload = (over = {}) => L.reload({ year, filters: filterParams(), ...over });
  useEffect(() => { if (fb.open) reload(); }, [fb.open]);

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* خواندن اطلاعات راننده هنگام تغییر خودرو */
  const onCarChange = async (carId) => {
    if (!carId) return;
    const j = await MAPI(`/driver/?car_id=${carId}&date=${form.request_date || todayFa()}`);
    if (j.ok) {
      F('driver', j.driver || '');
    }
  };

  /* خواندن مسئول واحد هنگام تغییر واحد */
  const onDeptChange = async (deptId) => {
    if (!deptId) return;
    const j = await MAPI(`/dept-manager/?department_id=${deptId}&date=${form.request_date || todayFa()}`);
    if (j.ok) {
      F('dept_manager', j.manager || '');
    }
  };

  /* آمادهٔ رکورد جدید */
  const prepareNew = async () => {
    const j = await MAPI('/prepare/');
    if (!j.ok) return;
    setForm({
      ...EMPTY(),
      serial_no: j.serial_no,
      request_no: j.request_no,
      request_date: j.request_date || todayFa(),
      insert_date: j.insert_date || todayFa(),
      insert_user: j.insert_user || '',
    });
    setErr('');
    setModal('new');
  };

  /* payload ساخت */
  const payload = () => ({
    car_id: form.car.id, department_id: form.department.id, city_id: form.city.id,
    manager_id: form.manager.id, serial_no: form.serial_no,
    request_no: form.request_no, request_date: form.request_date,
    mission_subject: form.mission_subject, from_date: form.from_date, to_date: form.to_date,
    mission_duration: form.mission_duration,
    department_agreement_date: form.department_agreement_date,
    manager_agreement_date: form.manager_agreement_date,
    done_confirmation_date: form.done_confirmation_date,
    exit_time: form.exit_time, enter_time: form.enter_time,
  });

  const save = async () => {
    setSaving(true); setErr('');
    const j = modal === 'new'
      ? await MAPI('/create/', { method: 'POST', body: JSON.stringify(payload()) })
      : await MAPI(`/${sel.CarMissionId}/update/`, { method: 'PUT', body: JSON.stringify(payload()) });
    setSaving(false);
    if (!j.ok) { setErr(j.msg); return; }
    setModal(null); reload();
  };

  const doDelete = async () => {
    setModal(null);
    const j = await MAPI(`/${sel.CarMissionId}/delete/`, { method: 'DELETE' });
    if (!j.ok) setMsg(j.msg); else { setSel(null); reload(); }
  };

  /* تغییر وضعیت تأیید */
  const toggleApproval = async (action, currentVal) => {
    const checked = !currentVal;
    const j = await MAPI(`/${sel.CarMissionId}/approve/`, {
      method: 'PUT',
      body: JSON.stringify({ action, checked }),
    });
    if (!j.ok) { setMsg(j.msg); return; }
    reload();
    setSel(null);
  };

  const report = async () => {
    try { await openReport('tadarokat.car_mission', { car_mission_id: sel.CarMissionId }); }
    catch (e) { setMsg(e.message); }
  };

  const has = !!sel && fb.open;
  const perms = sel?._perms || {};

  const toolbar = fb.open ? (
    <>
      <div style={ui.toolBtns}>
        {fb.can('BTNNEW') && <button onClick={prepareNew} style={ui.btnNew}>＋ جدید</button>}
        {fb.can('BTNEDIT') && <button disabled={!has} onClick={() => { setForm(rowToForm(sel)); setErr(''); setModal('edit'); }}
          style={{ ...ui.btnEdit, opacity: has ? 1 : 0.4 }}>✎ ویرایش</button>}
        {fb.can('BTNDELETE') && <button disabled={!has} onClick={() => setModal('delete')}
          style={{ ...ui.btnDel, opacity: has ? 1 : 0.4 }}>✕ حذف</button>}
        {fb.can('BTNREPORT') && <button disabled={!has || !perms.can_dept_agreement}
          onClick={report} style={{ ...smallBtn('#006064'), opacity: has ? 1 : 0.4 }}>📄 چاپ حکم</button>}
      </div>

      {/* دکمه‌های تأیید */}
      {has && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {perms.can_dept_agreement && (
            <button onClick={() => toggleApproval('department_agreement', !!sel['تاریخ موافقت مسئول واحد'])}
              style={smallBtn(sel['تاریخ موافقت مسئول واحد'] ? '#c62828' : '#2e7d32')}>
              {sel['تاریخ موافقت مسئول واحد'] ? '↩ لغو موافقت مسئول واحد' : '✓ موافقت مسئول واحد'}
            </button>
          )}
          {perms.can_manager_agreement && (
            <button onClick={() => toggleApproval('manager_agreement', !!sel['تاریخ موافقت موافقت کننده'])}
              style={smallBtn(sel['تاریخ موافقت موافقت کننده'] ? '#c62828' : '#1565c0')}>
              {sel['تاریخ موافقت موافقت کننده'] ? '↩ لغو موافقت موافقت‌کننده' : '✓ موافقت موافقت‌کننده'}
            </button>
          )}
          {perms.can_done_confirmation && (
            <button onClick={() => toggleApproval('done_confirmation', !!sel['تاریخ تایید انجام ماموریت'])}
              style={smallBtn(sel['تاریخ تایید انجام ماموریت'] ? '#c62828' : '#4a148c')}>
              {sel['تاریخ تایید انجام ماموریت'] ? '↩ لغو تأیید انجام' : '✓ تأیید انجام ماموریت'}
            </button>
          )}
        </div>
      )}

      <Field label="سال مالی" width="80px">
        <input value={year} onChange={e => setYear(Number(e.target.value) || '')}
          onKeyDown={e => e.key === 'Enter' && reload()} style={ui.yearInput} />
      </Field>

      {/* فیلترهای وضعیت */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '11px', alignItems: 'center' }}>
        <label><input type="checkbox" checked={showAll} onChange={e => { setShowAll(e.target.checked); setTimeout(() => reload({ filters: { ...filterParams(), show_all: e.target.checked ? '1' : '0' } }), 0); }} /> همه</label>
        {!showAll && <>
          <label><input type="checkbox" checked={isDeptAgr} onChange={e => setIsDeptAgr(e.target.checked)} /> موافقت واحد ✓</label>
          <label><input type="checkbox" checked={notDeptAgr} onChange={e => setNotDeptAgr(e.target.checked)} /> موافقت واحد ✗</label>
          <label><input type="checkbox" checked={isMgrAgr} onChange={e => setIsMgrAgr(e.target.checked)} /> موافقت م‌کننده ✓</label>
          <label><input type="checkbox" checked={notMgrAgr} onChange={e => setNotMgrAgr(e.target.checked)} /> موافقت م‌کننده ✗</label>
          <label><input type="checkbox" checked={isDone} onChange={e => setIsDone(e.target.checked)} /> تأیید انجام ✓</label>
          <label><input type="checkbox" checked={notDone} onChange={e => setNotDone(e.target.checked)} /> تأیید انجام ✗</label>
          <button onClick={() => reload()} style={smallBtn('#37474f')}>اعمال فیلتر</button>
        </>}
      </div>

      <CountBadge shown={L.data.length} total={L.pager.total} loading={L.loading} unit="ماموریت" />
      <ExcelButton title="مأموریت خودرو" columns={excelCols(cols, L.data)} rows={L.data} />
    </>
  ) : null;

  const grid = (
    <Grid columns={cols} rows={L.data} rowKey="CarMissionId" selectedKey={sel?.CarMissionId} onSelect={setSel}
      rowBg={r => ROW_COLORS[r._color] || null}
      empty={fb.loaded && !fb.open ? '' : 'ماموریتی یافت نشد'} />
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="مأموریت خودرو" subtitle="تدارکات — سامانه ارجان"
        toolbar={toolbar} error={L.error} pager={L.pager} onMore={L.more} loading={L.loading} unit="ماموریت">
        {grid}
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <Modal title={modal === 'new' ? '＋ ماموریت جدید' : '✎ ویرایش ماموریت'} onClose={() => setModal(null)} width="860px"
          footer={<>
            <button onClick={() => setModal(null)} style={ui.btnCancel}>لغو</button>
            {fb.can('BTNOK') && fb.can(modal === 'new' ? 'BTNNEW' : 'BTNEDIT') &&
              <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳ ...' : 'ثبت'}</button>}
          </>}>
          {err && <div style={ui.errBox}>⚠️ {err}</div>}
          <MissionForm form={form} F={F} mode={modal} onCarChange={onCarChange} onDeptChange={onDeptChange} />
        </Modal>
      )}

      {modal === 'delete' && <DeleteDialog onConfirm={doDelete} onCancel={() => setModal(null)} />}
      {msg && <MsgDialog text={msg} onClose={() => setMsg('')} />}
    </>
  );
}
